import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Plus, Users, Calendar, DollarSign, Settings, BarChart3, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NonEnrolledAdmin() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [registrations, setRegistrations] = useState([]);
  const [lessonSlots, setLessonSlots] = useState([]);
  const [lifeguards, setLifeguards] = useState([]);
  const [settings, setSettings] = useState({});
  const [summary, setSummary] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newLgName, setNewLgName] = useState("");
  const [newLgType, setNewLgType] = useState("");
  const [newSlot, setNewSlot] = useState({ day_of_week: "", time: "", lifeguard_id: "", price: "", max_capacity: "1" });

  const fetchData = async () => {
    try {
      const [regsRes, slotsRes, lgsRes, settingsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/admin/non-enrolled/registrations`),
        axios.get(`${API}/api/admin/non-enrolled/lesson-slots`),
        axios.get(`${API}/api/admin/non-enrolled/lifeguards`),
        axios.get(`${API}/api/admin/non-enrolled/settings`),
        axios.get(`${API}/api/admin/non-enrolled/reports/summary`),
      ]);
      setRegistrations(regsRes.data);
      setLessonSlots(slotsRes.data);
      setLifeguards(lgsRes.data);
      setSettings(settingsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error("Failed to fetch non-enrolled data", err);
    }
  };

  useEffect(() => { fetchData(); }, []);


  // ----- Lifeguard CRUD -----

  const addLifeguard = async () => {
    if (!newLgName.trim()) return;
    await axios.post(`${API}/api/admin/non-enrolled/lifeguards`, { name: newLgName, age_group: newLgType || null });
    setNewLgName(""); setNewLgType("");
    toast.success("Lifeguard added"); fetchData();
  };

  // ----- Slot CRUD -----

  const addSlot = async () => {
    if (!newSlot.day_of_week || !newSlot.time || !newSlot.lifeguard_id || !newSlot.price) {
      toast.error("Fill all slot fields"); return;
    }
    await axios.post(`${API}/api/admin/non-enrolled/lesson-slots`, {
      ...newSlot, price: parseFloat(newSlot.price), max_capacity: parseInt(newSlot.max_capacity) || 1
    });
    setNewSlot({ day_of_week: "", time: "", lifeguard_id: "", price: "", max_capacity: "1" });
    toast.success("Slot added"); fetchData();
  };

  const deleteSlot = async (id) => {
    await axios.delete(`${API}/api/admin/non-enrolled/lesson-slots/${id}`);
    toast.success("Slot deactivated"); fetchData();
  };

  // ----- Registration Actions -----
  const markPaid = async (id) => {
    await axios.post(`${API}/api/admin/non-enrolled/registrations/${id}/mark-paid`, { registration_id: id });
    toast.success("Marked as paid"); fetchData();
  };

  const cancelReg = async (id) => {
    await axios.post(`${API}/api/admin/non-enrolled/registrations/${id}/cancel`);
    toast.success("Cancelled"); fetchData();
  };

  // ----- Settings -----
  const saveSettings = async () => {
    await axios.put(`${API}/api/admin/non-enrolled/settings`, settings);
    toast.success("Settings saved"); fetchData();
  };

  const tabs = [
    { key: "dashboard", icon: BarChart3, label: "Dashboard" },
    { key: "registrations", icon: Users, label: "Registrations" },
    { key: "slots", icon: Calendar, label: "Lesson Slots" },
    { key: "lifeguards", icon: Users, label: "Lifeguards" },
    { key: "pricing", icon: DollarSign, label: "Pricing" },
    { key: "reports", icon: Download, label: "Reports" },
    { key: "settings", icon: Settings, label: "Settings" },
  ];

  const filteredRegs = registrations
    .filter(r => statusFilter === "all" || r.status === statusFilter)
    .filter(r => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return `${r.child_first_name} ${r.child_last_name}`.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q);
    })
    .sort((a, b) => `${a.child_last_name}`.localeCompare(`${b.child_last_name}`));

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 p-4 flex-shrink-0">
        <h2 className="font-bold text-[#D4A843] text-sm uppercase tracking-wider mb-4">Non-Enrolled</h2>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition-all ${
              activeTab === tab.key ? "bg-[#FDF6E3] text-[#92400E] font-bold" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
        <div className="mt-6 border-t pt-4">
          <a href="/admin/dashboard" className="text-xs text-slate-400 hover:text-slate-600">← Back to Enrolled Admin</a>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 overflow-y-auto">
        {/* Dashboard */}
        {activeTab === "dashboard" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-6">Non-Enrolled Dashboard</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="card text-center p-4"><p className="text-2xl font-bold text-[#2B4C8C]">{summary.total_registrations || 0}</p><p className="text-xs text-slate-500">Total</p></div>
              <div className="card text-center p-4"><p className="text-2xl font-bold text-yellow-600">{summary.tentative || 0}</p><p className="text-xs text-slate-500">Tentative</p></div>
              <div className="card text-center p-4"><p className="text-2xl font-bold text-green-600">{summary.confirmed || 0}</p><p className="text-xs text-slate-500">Confirmed</p></div>
              <div className="card text-center p-4"><p className="text-2xl font-bold text-[#D4A843]">${summary.total_revenue || 0}</p><p className="text-xs text-slate-500">Revenue</p></div>
            </div>
            <h2 className="font-semibold text-slate-700 mb-3">Recent Registrations</h2>
            <div className="space-y-2">
              {registrations.slice(0, 10).map(r => (
                <div key={r.id} className="card p-3 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm">{r.child_first_name} {r.child_last_name}</p>
                    <p className="text-xs text-slate-500">{r.choice1_day} {r.choice1_time} — {r.choice1_lifeguard_name}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${r.status === "confirmed" ? "bg-green-100 text-green-700" : r.status === "tentative" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                    {r.status?.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registrations */}
        {activeTab === "registrations" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-4">Non-Enrolled Registrations</h1>
            <div className="flex gap-3 mb-4">
              <Input data-testid="ne-admin-search" placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="max-w-xs" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="tentative">Tentative</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {filteredRegs.map(r => (
                <div key={r.id} className="card p-4 flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{r.child_first_name} {r.child_last_name}</p>
                    <p className="text-xs text-slate-500">{r.parent_first_name} {r.parent_last_name} — {r.email}</p>
                    <p className="text-xs text-slate-500">{r.choice1_day} {r.choice1_time} — {r.choice1_lifeguard_name} — ${r.total_price}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${r.status === "confirmed" ? "bg-green-100 text-green-700" : r.status === "tentative" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                      {r.status?.toUpperCase()}
                    </span>
                    {r.status === "tentative" && (
                      <Button size="sm" onClick={() => markPaid(r.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs">Mark Paid</Button>
                    )}
                    {r.status !== "cancelled" && r.status !== "expired" && (
                      <Button size="sm" variant="ghost" onClick={() => cancelReg(r.id)} className="text-red-500 text-xs">Cancel</Button>
                    )}
                  </div>
                </div>
              ))}
              {filteredRegs.length === 0 && <p className="text-slate-400 text-center py-8">No registrations found.</p>}
            </div>
          </div>
        )}

        {/* Lesson Slots */}
        {activeTab === "slots" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-4">Lesson Slots</h1>
            <div className="card p-4 mb-6">
              <h3 className="font-semibold text-sm mb-3">Add New Slot</h3>
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <Label className="text-xs">Day</Label>
                  <Select value={newSlot.day_of_week} onValueChange={v => setNewSlot(p => ({ ...p, day_of_week: v }))}>
                    <SelectTrigger className="w-32"><SelectValue placeholder="Day" /></SelectTrigger>
                    <SelectContent>
                      {["monday","tuesday","wednesday","thursday","friday","saturday"].map(d => (
                        <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input className="w-24" placeholder="8:30am" value={newSlot.time} onChange={e => setNewSlot(p => ({ ...p, time: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Lifeguard</Label>
                  <Select value={newSlot.lifeguard_id} onValueChange={v => setNewSlot(p => ({ ...p, lifeguard_id: v }))}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {lifeguards.filter(l => l.active).map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Price ($)</Label>
                  <Input className="w-20" type="number" value={newSlot.price} onChange={e => setNewSlot(p => ({ ...p, price: e.target.value }))} />
                </div>
                <Button onClick={addSlot} className="bg-[#D4A843] hover:bg-[#b8902e] text-white"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
            </div>
            <div className="space-y-2">
              {lessonSlots.filter(s => s.is_active !== false).map(s => (
                <div key={s.id} className="card p-3 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm">{s.day_of_week?.charAt(0).toUpperCase() + s.day_of_week?.slice(1)} — {s.time}</p>
                    <p className="text-xs text-slate-500">{s.lifeguard_name} — ${s.price}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteSlot(s.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              {lessonSlots.filter(s => s.is_active !== false).length === 0 && <p className="text-slate-400 text-center py-8">No slots configured. Add one above.</p>}
            </div>
          </div>
        )}

        {/* Lifeguards */}
        {activeTab === "lifeguards" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-4">Non-Enrolled Lifeguards</h1>
            <div className="card p-4 mb-6">
              <h3 className="font-semibold text-sm mb-3">Add Lifeguard</h3>
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={newLgName} onChange={e => setNewLgName(e.target.value)} placeholder="Name" className="w-48" />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={newLgType} onValueChange={setNewLgType}>
                    <SelectTrigger className="w-28"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Adult">Adult</SelectItem>
                      <SelectItem value="Teen">Teen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addLifeguard} className="bg-[#D4A843] hover:bg-[#b8902e] text-white"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
            </div>
            <div className="space-y-2">
              {lifeguards.map(lg => (
                <div key={lg.id} className="card p-3 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm">{lg.name}</p>
                    <p className="text-xs text-slate-500">{lg.age_group || "N/A"} — {lg.active ? "Active" : "Inactive"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pricing */}
        {activeTab === "pricing" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-4">Default Pricing</h1>
            <div className="card p-6 max-w-md space-y-4">
              <div>
                <Label>Mon-Thu Default ($)</Label>
                <Input type="number" value={settings.default_price_mth || ""} onChange={e => setSettings(p => ({ ...p, default_price_mth: parseFloat(e.target.value) }))} />
              </div>
              <div>
                <Label>Friday Default ($)</Label>
                <Input type="number" value={settings.default_price_fri || ""} onChange={e => setSettings(p => ({ ...p, default_price_fri: parseFloat(e.target.value) }))} />
              </div>
              <div>
                <Label>Saturday Default ($)</Label>
                <Input type="number" value={settings.default_price_sat || ""} onChange={e => setSettings(p => ({ ...p, default_price_sat: parseFloat(e.target.value) }))} />
              </div>
              <Button onClick={saveSettings} className="bg-[#2B4C8C] hover:bg-[#1e3666] text-white w-full">Save Pricing</Button>
            </div>
          </div>
        )}

        {/* Reports */}
        {activeTab === "reports" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-4">Non-Enrolled Reports</h1>
            <div className="card p-6 max-w-md">
              <p className="text-sm text-slate-600 mb-4">Download lifeguard earnings report for non-enrolled lessons.</p>
              <Button onClick={() => window.open(`${API}/api/admin/non-enrolled/reports/earnings-xlsx`, '_blank')} className="bg-[#2B4C8C] hover:bg-[#1e3666] text-white w-full">
                <Download className="w-4 h-4 mr-2" /> Download Earnings XLSX
              </Button>
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === "settings" && (
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-4">Non-Enrolled Settings</h1>
            <div className="card p-6 max-w-md space-y-4">
              <div className="flex items-center justify-between">
                <Label>Registration Open</Label>
                <input type="checkbox" checked={settings.registration_open ?? true} onChange={e => setSettings(p => ({ ...p, registration_open: e.target.checked }))} className="w-5 h-5" />
              </div>
              <div>
                <Label>Tentative Hold (days)</Label>
                <Input type="number" value={settings.tentative_hold_days || 7} onChange={e => setSettings(p => ({ ...p, tentative_hold_days: parseInt(e.target.value) }))} />
              </div>
              <div>
                <Label>Max Capacity Per Slot</Label>
                <Input type="number" value={settings.max_capacity_per_slot || 1} onChange={e => setSettings(p => ({ ...p, max_capacity_per_slot: parseInt(e.target.value) }))} />
              </div>
              <Button onClick={saveSettings} className="bg-[#2B4C8C] hover:bg-[#1e3666] text-white w-full">Save Settings</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
